import { modalActions } from '@Root/store'

const MAX_ERROR_DETAILS = 3

export const confirmDeleteAction = (dispatch, { text, checkboxLabel = null, requireCheckbox = false }) =>
  new Promise((resolve, reject) => {
    dispatch(
      modalActions.showModal('ConfirmationModal', {
        text,
        checkboxLabel,
        requireCheckbox,
        clickRejectButtonHandler: reject,
        clickResolveButtonHandler: (includeSynced = false) => resolve(includeSynced),
      })
    )
  })

export const formatBulkErrors = (errors = []) => {
  if (!errors.length) {
    return ''
  }

  const visibleErrors = errors.slice(0, MAX_ERROR_DETAILS)
  const details = visibleErrors.map(({ id, message }) => `#${id}: ${message}`).join('; ')
  const hiddenCount = errors.length - visibleErrors.length

  if (hiddenCount > 0) {
    return `${details}; and ${hiddenCount} more`
  }

  return details
}

export const notifyBulkResult = ({ deleted, failed, errors = [], entityLabel, onSuccess, onError }) => {
  const failedCount = Math.max(failed ?? 0, errors.length)
  const errorDetails = formatBulkErrors(errors)
  const errorSuffix = errorDetails ? `: ${errorDetails}` : ''

  if (failedCount > 0) {
    const message =
      deleted > 0
        ? `Delete partially completed: ${deleted} deleted, ${failedCount} failed${errorSuffix}`
        : `Failed to delete ${failedCount} ${entityLabel}(s)${errorSuffix}`

    if (deleted > 0) {
      onSuccess(message)
      return
    }

    onError(new Error(message))
    return
  }

  onSuccess(`Delete completed: ${deleted} ${entityLabel}(s) deleted`)
}

export const runConfirmedBulkDelete = async ({
  dispatch,
  formId,
  ids,
  confirmText,
  deleteRequest,
  entityLabel,
  onSuccess,
  onError,
}) => {
  if (!ids.length) {
    return null
  }

  let includeSynced = false

  try {
    includeSynced = await confirmDeleteAction(dispatch, {
      text: confirmText,
      checkboxLabel: 'Also delete synced submissions',
    })
  } catch {
    dispatch(modalActions.hideModal())
    return null
  }

  dispatch(modalActions.hideModal())

  let payload

  try {
    const response = await deleteRequest(formId, ids, includeSynced)
    payload = response.data?.data ?? response.data ?? response
  } catch (error) {
    payload = error.response?.data?.data ?? error.response?.data

    if (!payload || (payload.deleted === undefined && payload.failed === undefined)) {
      throw error
    }
  }

  notifyBulkResult({
    deleted: payload.deleted ?? 0,
    failed: payload.failed ?? 0,
    errors: payload.errors ?? [],
    entityLabel,
    onSuccess,
    onError,
  })

  return payload
}
